import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Trash, Search } from 'react-bootstrap-icons';
import { Spinner, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import DashboardLayout from '../components/DashboardLayout';
import ResumeUploadForm from '../components/ResumeUploadForm';
import { apiRequest } from '../utils/api';
import { resolveFileFromCandidates } from '../utils/fileUrl';
import { getActiveCompanyId } from '../utils/companyContext';
import { useNavigate } from 'react-router-dom';
import { getModulePermissions } from '../utils/permissions';
import '../assets/styles/files.css';

const Resume = () => {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [resumes, setResumes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());

  const permissions = useMemo(() => getModulePermissions('employee.resume'), []);
  const canUpload = permissions.create;
  const canDelete = permissions.delete;

  const actionButtonGroupClass = useMemo(
    () =>
      [
        'd-flex',
        'flex-column',
        'flex-md-row',
        'justify-content-md-end',
        'align-items-stretch',
        'align-items-md-center',
        'gap-3',
        'mb-4',
      ].join(' '),
    [],
  );

  const fetchResumes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!activeCompanyId) {
        setResumes([]);
        setError('Select a company to view resumes.');
        return;
      }

      const params = new URLSearchParams();
      params.set('companyId', activeCompanyId);
      if (searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }

      const { data } = await apiRequest(`/api/admin/resumes?${params.toString()}`);
      setResumes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load resumes.');
      setResumes([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompanyId, searchTerm]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchResumes();
    }, 400);

    return () => clearTimeout(timeout);
  }, [fetchResumes]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const handleResumeUploaded = useCallback(() => {
    fetchResumes();
  }, [fetchResumes]);

  const handleDelete = async (resumeId) => {
    if (!canDelete) {
      window.alert('You do not have permission to delete resumes.');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete resume?',
      text: 'Are you sure you want to delete this resume? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await apiRequest(`/api/admin/resumes/${resumeId}`, { method: 'DELETE' });
      setResumes((prev) => prev.filter((resume) => resume.id !== resumeId));
    } catch (deleteError) {
      window.alert(deleteError.message || 'Failed to delete the resume.');
    }
  };

  const sortedResumes = useMemo(
    () =>
      [...resumes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [resumes],
  );

  return (
    <DashboardLayout>
      <div className="files-page container-fluid py-4">
        <div className={actionButtonGroupClass}>
          {canUpload && (
            <button
              className="files-pill-button d-flex align-items-center justify-content-center"
              onClick={() => setShowUploadModal(true)}
              type="button"
            >
              Upload New Resume
            </button>
          )}
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files')}
            type="button"
          >
            Files
          </button>
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files/names')}
            type="button"
          >
            File Names
          </button>
        </div>

        <div className="files-filters shadow-sm mb-4">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label text-muted small fw-semibold text-uppercase">
                Search Resumes
              </label>
              <div className="files-search-control">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  className="form-control files-input"
                  placeholder="Search by name, designation, phone or CRM source"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card files-table-card shadow-sm">
          <div className="card-body p-0">
            {isLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <div className="py-5 px-3">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0 files-table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '10%' }}>#</th>
                      <th>CRM</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Phone No</th>
                      <th>Uploaded On</th>
                      <th className="text-center" style={{ width: '12%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResumes.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No resumes found.
                        </td>
                      </tr>
                    ) : (
                      sortedResumes.map((resume, index) => (
                        <tr key={resume.id}>
                          <td>{index + 1}</td>
                          <td>{resume.crmSource || '-'}</td>
                          <td className="fw-semibold">{resume.name}</td>
                          <td>{resume.designation || '-'}</td>
                          <td>{resume.phone || '-'}</td>
                          <td>{new Date(resume.createdAt).toLocaleString()}</td>
                          <td>
                            <div className="files-action-buttons d-flex justify-content-center gap-2">
                              <a
                                className="btn btn-icon"
                                href={resolveFileFromCandidates(resume.fileUrl, resume.filePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`View ${resume.name || 'resume'}`}
                              >
                                <Eye size={18} />
                              </a>
                              {canDelete && (
                                <button
                                  className="btn btn-icon"
                                  type="button"
                                  onClick={() => handleDelete(resume.id)}
                                  aria-label={`Delete ${resume.name || 'resume'}`}
                                >
                                  <Trash size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <ResumeUploadForm
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          onUploaded={handleResumeUploaded}
          companyId={activeCompanyId}
        />

        <div className="mt-4 d-flex justify-content-center">
          <button
            type="button"
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files')}
          >
            Back to Files
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Resume;
